import { create } from "zustand";
import type { Object3D } from "three";
import type { ObjectName, Quality, Section, Theme } from "@/types";
import type { Pose } from "@/scene/cameras/cameraTargets";

interface PendingMove {
  pose: Pose;
  duration: number;
  id: number;
}

interface SceneStore {
  theme: Theme;
  setTheme: (t: Theme) => void;
  activeSection: Section | null;
  setActiveSection: (s: Section | null) => void;
  activeMonitorRoute: string | null;
  setActiveMonitorRoute: (r: string | null) => void;
  cameraTarget: string | null;
  setCameraTarget: (k: string | null) => void;
  pendingCameraMove: PendingMove | null;
  requestCameraMove: (pose: Pose, duration?: number) => void;
  focusedObject: ObjectName | null;
  setFocusedObject: (id: ObjectName | null) => void;
  hoveredObject: ObjectName | null;
  setHoveredObject: (id: ObjectName | null) => void;
  objects: Partial<Record<ObjectName, Object3D>>;
  registerObject: (name: ObjectName, ref: Object3D) => void;
  quality: Quality;
  setQuality: (q: Quality) => void;
  isCanvasReady: boolean;
  setCanvasReady: (b: boolean) => void;
  plantBlurbOpen: boolean;
  togglePlantBlurb: () => void;
  cpuBlurbOpen: boolean;
  toggleCpuBlurb: () => void;
  mugBlurbOpen: boolean;
  toggleMugBlurb: () => void;
}

type WindowWithStore = Window & { __scene?: unknown };

export const useSceneStore = create<SceneStore>((set) => ({
  theme: "dark",
  setTheme: (t) => set({ theme: t }),
  activeSection: null,
  setActiveSection: (s) => set({ activeSection: s }),
  activeMonitorRoute: null,
  setActiveMonitorRoute: (r) => set({ activeMonitorRoute: r }),
  cameraTarget: null,
  setCameraTarget: (k) => set({ cameraTarget: k }),
  pendingCameraMove: null,
  requestCameraMove: (pose, duration = 1.3) =>
    set({ pendingCameraMove: { pose, duration, id: Date.now() } }),
  focusedObject: null,
  setFocusedObject: (id) => set({ focusedObject: id }),
  hoveredObject: null,
  setHoveredObject: (id) => set({ hoveredObject: id }),
  objects: {},
  registerObject: (name, ref) =>
    set((s) => ({ objects: { ...s.objects, [name]: ref } })),
  quality: "med",
  setQuality: (q) => set({ quality: q }),
  isCanvasReady: false,
  setCanvasReady: (b) => set({ isCanvasReady: b }),
  plantBlurbOpen: false,
  togglePlantBlurb: () => set((s) => ({ plantBlurbOpen: !s.plantBlurbOpen })),
  cpuBlurbOpen: false,
  toggleCpuBlurb: () => set((s) => ({ cpuBlurbOpen: !s.cpuBlurbOpen })),
  mugBlurbOpen: false,
  toggleMugBlurb: () => set((s) => ({ mugBlurbOpen: !s.mugBlurbOpen })),
}));

if (typeof window !== "undefined" && import.meta.env.DEV) {
  (window as WindowWithStore).__scene = useSceneStore;
}
